import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { Loading } from '../components/Loading';
import { EmptyState } from '../components/EmptyState';

const STATUS_LABELS = {
  pending: { label: 'Pendente', color: 'var(--nc-warning-text)', bg: 'var(--nc-warning-soft)' },
  accepted: { label: 'Aceito', color: 'var(--nc-primary)', bg: 'var(--nc-success-soft)' },
  picked_up: { label: 'Em entrega', color: '#1e4d8c', bg: '#eff6ff' },
  delivered: { label: 'Entregue', color: 'var(--nc-success-text)', bg: 'var(--nc-success-soft)' },
  cancelled: { label: 'Cancelado', color: 'var(--nc-danger-text)', bg: 'var(--nc-danger-soft)' },
  declined: { label: 'Recusado', color: 'var(--nc-text-secondary)', bg: 'var(--nc-surface-alt)' },
};

// LE-025: estados terminais (não evoluem) — o polling para neles.
const TERMINAL_STATUSES = new Set(['delivered', 'cancelled']);

// LE-025: polling do status do pedido — mesmo padrão conservador do LE-024
// (30s, pausa com aba oculta, sem sobrepor requisição em voo, refresh silencioso).
const POLL_INTERVAL_MS = 30000;

const fmtDateTime = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
};

const formatMoney = (v) => (v == null ? null : `R$ ${Number(v).toFixed(2).replace('.', ',')}`);

// LE-020 (S4-a): seletor de estrelas com preview no hover
function StarPicker({ value, onChange, disabled }) {
  const [hover, setHover] = useState(null);
  const display = hover ?? value;
  return (
    <div style={{ display: 'flex', gap: 4 }} role="radiogroup" aria-label="Nota">
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          type="button"
          disabled={disabled}
          onClick={() => onChange(n)}
          onMouseEnter={() => !disabled && setHover(n)}
          onMouseLeave={() => !disabled && setHover(null)}
          aria-label={`${n} ${n === 1 ? 'estrela' : 'estrelas'}`}
          style={{
            fontSize: 28, lineHeight: 1, border: 'none', background: 'none', padding: 2,
            cursor: disabled ? 'default' : 'pointer',
            color: n <= display ? '#f59e0b' : '#d1d5db',
            transition: 'transform 0.12s ease, color 0.12s ease',
            transform: !disabled && n <= display ? 'scale(1.12)' : 'scale(1)',
          }}
        >★</button>
      ))}
    </div>
  );
}

// LE-018: linha do tempo do pedido — eventos vêm do backend (order.events),
// ordenados por timestamp; estados sem registro de horário são omitidos.
function Timeline({ events }) {
  if (!events || events.length === 0) return null;
  return (
    <ol style={{ listStyle: 'none', margin: 0, padding: 0, position: 'relative' }}>
      {events.map((e, i) => {
        const isLast = i === events.length - 1;
        return (
          <li key={`${e.at}-${i}`} style={{ position: 'relative', paddingLeft: 34, paddingBottom: isLast ? 0 : 18 }}>
            <div style={{
              position: 'absolute', left: 6, top: 2, width: 22, height: 22, borderRadius: '50%',
              background: 'var(--nc-success-soft)', border: '2px solid var(--nc-primary)', display: 'flex',
              alignItems: 'center', justifyContent: 'center', fontSize: 11,
            }}>
              {e.emoji}
            </div>
            {!isLast && (
              <div style={{
                position: 'absolute', left: 16, top: 26, width: 2, height: 'calc(100% - 18px)',
                background: '#d1fae5',
              }} />
            )}
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nc-text)' }}>{e.label}</div>
            <div className="nc-caption">{fmtDateTime(e.at)}</div>
          </li>
        );
      })}
    </ol>
  );
}

export function OrderDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, isVisitor } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  // LE-019: estado do "Refazer pedido" (pedido expirado na fila)
  const [redoing, setRedoing] = useState(false);
  // LE-020 (S4-a): avaliação do entregador (pedidos entregues)
  // 'loading' inicial evita o flicker do formulário antes do GET de verificação
  const [reviewState, setReviewState] = useState('loading'); // loading | ready | done
  const [myReview, setMyReview] = useState(null);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewMsg, setReviewMsg] = useState(null);
  const [lastSync, setLastSync] = useState(null); // LE-025: "Atualizado às HH:MM"
  const [syncWarning, setSyncWarning] = useState(false); // LE-025: falha de poll silencioso
  const [statusNotice, setStatusNotice] = useState(null); // LE-025: "O pedido mudou para X"
  const statusRef = useRef(null); // LE-025: status do último sync (null = 1º load)
  // LE-025: timer do aviso de mudança de status — limpo no unmount
  const noticeTimerRef = useRef(null);
  useEffect(() => () => {
    if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
  }, []);

  // LE-025: `silent` = refresh automático (sem spinner/erro em tela cheia; dados
  // antigos permanecem se a rede falhar). Ação do usuário/primeiro load = não-silent.
  const fetchOrder = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const { data } = await api.get(`/orders/${id}`);
      const next = data.order;
      // LE-025: status mudou desde o último sync → aviso sutil (auto-dismiss)
      if (silent && statusRef.current !== null && statusRef.current !== next.status) {
        const meta = STATUS_LABELS[next.status] || { label: next.status };
        setStatusNotice(meta.label);
        if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
        noticeTimerRef.current = setTimeout(() => setStatusNotice(null), 6000);
      }
      statusRef.current = next.status;
      // Poll silencioso com sucesso = dados frescos — limpa erro de um load
      // inicial que tenha falhado (senão a tela ficaria presa no erro).
      if (silent) {
        setError(null);
        setSyncWarning(false);
      }
      setOrder(next);
      setLastSync(new Date());
    } catch (err) {
      // 404 (pedido inexistente ou de outro usuário) → estado amigável (mesmo
      // em poll silencioso: o pedido sumiu, parar de insistir é o correto)
      if (err.response?.status === 404) {
        setOrder(null);
        setError(null);
      } else if (silent) {
        // Refresh automático falhou: mantém o que está na tela e informa com leveza
        setSyncWarning(true);
      } else {
        setError(err.response?.data?.error?.message || 'Erro ao carregar o pedido');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchOrder(); }, [fetchOrder]);

  // LE-025: polling — só enquanto o pedido não estiver em estado terminal;
  // pausa com a aba oculta; ao voltar a ficar visível, sincroniza na hora.
  const isActive = !!order && !TERMINAL_STATUSES.has(order.status);
  useEffect(() => {
    if (!isActive) return undefined;

    let inFlight = false;
    const sync = async () => {
      if (document.visibilityState === 'hidden' || inFlight) return;
      inFlight = true;
      try {
        await fetchOrder({ silent: true });
      } finally {
        inFlight = false;
      }
    };
    const onVisibility = () => { if (document.visibilityState === 'visible') sync(); };

    const timer = setInterval(sync, POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchOrder, isActive]);

  // LE-019: refaz o pedido expirado (POST /orders/:id/redo) e navega para o
  // novo pedido, que nasce pendente.
  const handleRedo = async () => {
    setRedoing(true);
    setError(null);
    try {
      const { data } = await api.post(`/orders/${id}/redo`);
      navigate(`/pedido/${data.order.id}`);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Erro ao refazer pedido');
    } finally {
      setRedoing(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancelar este pedido de entrega?')) return;
    setCancelling(true);
    setError(null);
    try {
      await api.post(`/orders/${id}/cancel`);
      await fetchOrder();
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Erro ao cancelar pedido');
    } finally {
      setCancelling(false);
    }
  };

  // LE-020 (S4-a): só avalia pedido entregue com entregador e usuário logado
  const canReview = !!order && order.status === 'delivered' && !!order.delivery_id && !isVisitor;

  // Carrega avaliações do entregador para saber se o usuário já avaliou
  useEffect(() => {
    if (!canReview) return;
    let cancelled = false;
    (async () => {
      setReviewState('loading');
      try {
        const { data } = await api.get(`/deliveries/${order.delivery_id}/reviews`);
        if (cancelled) return;
        const mine = (data.data || []).find(r => r.user_id === user?.id);
        if (mine) {
          setMyReview({ rating: mine.rating, comment: mine.comment || '' });
          setReviewState('done');
        } else {
          setReviewState('ready');
        }
      } catch {
        if (!cancelled) setReviewState('ready');
      }
    })();
    return () => { cancelled = true; };
  }, [canReview]); // eslint-disable-line

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (reviewRating < 1) {
      setReviewMsg({ type: 'err', text: 'Escolha uma nota de 1 a 5 estrelas.' });
      return;
    }
    setReviewSubmitting(true);
    setReviewMsg(null);
    try {
      await api.post(`/deliveries/${order.delivery_id}/reviews`, {
        rating: reviewRating,
        comment: reviewComment.trim() || undefined,
      });
      setMyReview({ rating: reviewRating, comment: reviewComment.trim() });
      setReviewState('done');
      setReviewMsg({ type: 'ok', text: 'Avaliação enviada! Obrigado por ajudar a comunidade.' });
    } catch (err) {
      const code = err.response?.data?.error?.code;
      if (code === 'ALREADY_REVIEWED') {
        // Corrida rara: a avaliação foi criada entre o GET de verificação e o
        // POST (ex.: outra aba). Rebusca para exibir a avaliação registrada —
        // sem isso o guard `&& myReview` deixaria o formulário no ar.
        setReviewMsg({ type: 'info', text: 'Você já avaliou este entregador.' });
        try {
          const { data } = await api.get(`/deliveries/${order.delivery_id}/reviews`);
          const mine = (data.data || []).find(r => r.user_id === user?.id);
          setMyReview(mine ? { rating: mine.rating, comment: mine.comment || '' } : null);
        } catch {
          setMyReview({ rating: reviewRating, comment: reviewComment.trim() });
        }
        setReviewState('done');
      } else {
        setReviewMsg({ type: 'err', text: err.response?.data?.error?.message || 'Erro ao enviar avaliação. Tente novamente.' });
      }
    } finally {
      setReviewSubmitting(false);
    }
  };

  // LE-019: pedido expirado na fila = 'cancelled' + expired_at — chip "Expirado"
  const isExpired = order?.status === 'cancelled' && !!order?.expired_at;
  const statusMeta = isExpired
    ? { label: 'Expirado', color: 'var(--nc-warning-text)', bg: 'var(--nc-warning-soft)' }
    : STATUS_LABELS[order?.status] || { label: order?.status || '—', color: '#333', bg: 'var(--nc-surface)' };

  return (
    <div className="nc-page nc-page--pad">
      <div className="nc-pagehead">
        <div className="nc-row">
          <button onClick={() => navigate('/pedidos')} aria-label="Voltar" className="nc-iconbtn">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="nc-page-title">{order ? `Pedido #${order.id}` : 'Pedido'}</h1>
          {order && (
            <span style={{
              marginLeft: 'auto', fontSize: 11, fontWeight: 600,
              color: statusMeta.color, background: 'var(--nc-surface)',
              padding: '3px 10px', borderRadius: 12,
            }}>
              {statusMeta.label}
            </span>
          )}
        </div>
        {/* LE-025: indicador de atualização automática (polling 30s) */}
        {lastSync && !loading && (
          <div style={{ fontSize: 11, color: '#d1fae5', marginTop: 6 }}>
            🔄 Atualizado às {lastSync.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
          </div>
        )}
      </div>

      <div style={{ padding: 16 }}>
        {loading ? <Loading message="Carregando pedido..." /> : error ? (
          <div role="alert" style={{
            padding: 14, borderRadius: 10, background: 'var(--nc-danger-soft)', border: '1px solid var(--nc-danger-border)',
            color: 'var(--nc-danger-text)', fontSize: 13
          }}>
            {error}
            <button onClick={fetchOrder} className="nc-btn-primary" style={{ marginTop: 10, width: '100%' }}>Tentar novamente</button>
          </div>
        ) : !order ? (
          <EmptyState icon="📋" title="Pedido não encontrado" description="Este pedido não existe ou não pertence à sua conta." />
        ) : (
          <>
            {/* LE-025: o status do pedido mudou durante um poll silencioso */}
            {statusNotice && (
              <div role="status" style={{
                padding: 10, borderRadius: 10, background: 'var(--nc-success-soft)', border: '1px solid var(--nc-success-border)',
                color: 'var(--nc-primary)', fontSize: 12.5, marginBottom: 12
              }}>
                🔄 O status do seu pedido mudou para <strong>{statusNotice}</strong>.
              </div>
            )}
            {/* LE-025: falha transitória do polling silencioso — aviso neutro, não verde */}
            {syncWarning && (
              <div role="status" style={{
                padding: 10, borderRadius: 10, background: 'var(--nc-warning-soft)', border: '1px solid var(--nc-warning-border)',
                color: 'var(--nc-warning-text)', fontSize: 12, marginBottom: 12
              }}>
                ⚠️ Falha ao atualizar o pedido. Tentando novamente...
              </div>
            )}
            <div style={{ background: 'var(--nc-surface)', borderRadius: 12, border: '1px solid var(--nc-border)', padding: 14, marginBottom: 12 }}>
              {order.business_name && (
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nc-text)', marginBottom: 4 }}>🏪 {order.business_name}</div>
              )}
              <div style={{ fontSize: 13, color: 'var(--nc-text-strong)', marginBottom: 4 }}>📍 {order.delivery_address}</div>
              {order.pickup_address && (
                <div style={{ fontSize: 12, color: 'var(--nc-text-muted)', marginBottom: 4 }}>🛍️ Retirada: {order.pickup_address}</div>
              )}
              {order.delivery_person_name ? (
                <div className="nc-hint" style={{ marginBottom: 4 }}>🛵 Entregador: {order.delivery_person_name}</div>
              ) : order.status === 'declined' ? (
                <div style={{ fontSize: 12, color: 'var(--nc-warning-text)', marginBottom: 4 }}>✋ Aguardando outro entregador aceitar seu pedido.</div>
              ) : null}
              {order.notes && (
                <div style={{ fontSize: 12, color: 'var(--nc-text-muted)', marginBottom: 4 }}>📝 {order.notes}</div>
              )}
              {order.total_amount != null && (
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--nc-primary)', marginTop: 4 }}>{formatMoney(order.total_amount)}</div>
              )}
              {/* LE-019: aviso de expiração — pedido cancelado automaticamente */}
              {isExpired && (
                <div style={{
                  marginTop: 10, padding: '10px 12px', borderRadius: 10,
                  background: 'var(--nc-warning-soft)', border: '1px solid var(--nc-warning-border)', fontSize: 12.5, color: '#92400e'
                }}>
                  ⏰ Nenhum entregador aceitou seu pedido a tempo e ele foi cancelado automaticamente.
                  Refazer pede a mesma entrega ao mesmo entregador — se ele recusar de novo, outro
                  entregador pode aceitar.
                </div>
              )}
            </div>

            <h2 style={{ fontSize: 14, fontWeight: 600, color: 'var(--nc-text)', margin: '0 0 10px' }}>Histórico do pedido</h2>
            <div style={{ background: 'var(--nc-surface)', borderRadius: 12, border: '1px solid var(--nc-border)', padding: 16, marginBottom: 12 }}>
              <Timeline events={order.events} />
            </div>

            {/* LE-020 (S4-a): avaliação do entregador após entrega concluída */}
            {canReview && (
              <div style={{ background: 'var(--nc-surface)', borderRadius: 12, border: '1px solid var(--nc-border)', padding: 16, marginBottom: 12 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--nc-text)', marginBottom: 4 }}>⭐ Avaliar entregador</div>
                <div className="nc-hint" style={{ marginBottom: 12 }}>
                  {order.delivery_person_name || 'O entregador'} concluiu a entrega — como foi a experiência?
                </div>

                {reviewState === 'loading' ? (
                  <Loading message="Carregando avaliação..." />
                ) : reviewState === 'done' && myReview ? (
                  <div>
                    <StarPicker value={myReview.rating} onChange={() => {}} disabled />
                    {myReview.comment && (
                      <div style={{ fontSize: 13, color: 'var(--nc-text-strong)', marginTop: 8 }}>“{myReview.comment}”</div>
                    )}
                    <div style={{ fontSize: 12, color: 'var(--nc-success-text)', marginTop: 8 }}>✓ Avaliação registrada</div>
                  </div>
                ) : (
                  <form onSubmit={handleReviewSubmit}>
                    <div style={{ marginBottom: 10 }}>
                      <StarPicker value={reviewRating} onChange={setReviewRating} />
                    </div>
                    <textarea
                      value={reviewComment}
                      onChange={e => setReviewComment(e.target.value)}
                      rows={2}
                      maxLength={1000}
                      placeholder="Como foi a entrega? (opcional)"
                      aria-label="Comentário da avaliação"
                      style={{
                        width: '100%', padding: '10px 12px', borderRadius: 10,
                        border: '1px solid var(--nc-border)', fontSize: 13, marginBottom: 10,
                        outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box',
                      }}
                    />
                    {reviewMsg && (
                      <div role="status" style={{
                        padding: '9px 12px', borderRadius: 8, marginBottom: 10, fontSize: 12,
                        background: reviewMsg.type === 'err' ? '#fef2f2' : reviewMsg.type === 'ok' ? '#f0fdf4' : '#eff6ff',
                        border: `1px solid ${reviewMsg.type === 'err' ? '#fecaca' : reviewMsg.type === 'ok' ? '#bbf7d0' : '#bfdbfe'}`,
                        color: reviewMsg.type === 'err' ? '#b91c1c' : reviewMsg.type === 'ok' ? '#15803d' : '#1e4d8c',
                      }}>
                        {reviewMsg.text}
                      </div>
                    )}
                    <button
                      type="submit"
                      disabled={reviewSubmitting}
                      className="nc-btn-primary"
                      style={{ width: '100%', opacity: reviewSubmitting ? 0.7 : 1 }}
                    >
                      {reviewSubmitting ? 'Enviando...' : 'Enviar avaliação'}
                    </button>
                  </form>
                )}
              </div>
            )}

            {(order.status === 'pending' || order.status === 'declined') && (
              <button
                onClick={handleCancel}
                disabled={cancelling}
                style={{
                  width: '100%', padding: '11px 12px', borderRadius: 10,
                  background: 'var(--nc-danger-soft)', color: 'var(--nc-danger-text)', border: '1px solid var(--nc-danger-border)',
                  fontSize: 13, fontWeight: 600, cursor: cancelling ? 'wait' : 'pointer'
                }}
              >
                {cancelling ? 'Cancelando...' : 'Cancelar pedido'}
              </button>
            )}
            {/* LE-019: pedido expirado — refazer com um toque */}
            {isExpired && (
              <button
                onClick={handleRedo}
                disabled={redoing}
                className="nc-btn-primary"
                style={{ width: '100%', opacity: redoing ? 0.7 : 1 }}
              >
                {redoing ? 'Refazendo...' : 'Refazer pedido'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
